import { createContext, useReducer, useCallback, useEffect, useRef, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTutorialSteps, type TutorialStep } from '@/components/tutorial/tutorial-steps';
import { useContractsData } from '@/hooks/api/use-contracts';
import useIsPWA from '@/hooks/use-is-pwa';
import { hasContractApporteur43397 } from '@/lib/contracts';
import TutorialOverlay from '@/components/tutorial/tutorial-overlay';
import WelcomePopup from '@/components/tutorial/welcome-popup';
import FinishPopup from '@/components/tutorial/finish-popup';

export interface TutorialContextValue {
  isActive: boolean;
  currentStep: number;
  currentStepData: TutorialStep | null | undefined;
  waitingForInteraction: boolean;
  totalSteps: number;
  startTutorial: () => void;
  requestTutorial: () => void;
  showWelcome: boolean;
  setShowWelcome: (show: boolean) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTutorial: () => void;
  showFinish: boolean | undefined;
  dismissFinish: () => void;
  getTargetSelector: (step: TutorialStep | null | undefined) => string | null;
}

export const TutorialContext = createContext<TutorialContextValue | null>(null);

const STORAGE_KEY = 'perso-v3-tutorial-completed';

interface TutorialState {
  isActive: boolean;
  currentStep: number;
  waitingForInteraction: boolean;
  showFinish?: boolean;
}

type TutorialAction =
  | { type: 'START' }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'WAIT_INTERACTION' }
  | { type: 'FINISH' }
  | { type: 'STOP' }
  | { type: 'DISMISS_FINISH' };

const initialState: TutorialState = {
  isActive: false,
  currentStep: 0,
  waitingForInteraction: false,
  showFinish: false,
};

function tutorialReducer(state: TutorialState, action: TutorialAction): TutorialState {
  switch (action.type) {
    case 'START':
      return { isActive: true, currentStep: 0, waitingForInteraction: false };
    case 'NEXT_STEP':
      return { ...state, currentStep: state.currentStep + 1, waitingForInteraction: false };
    case 'PREV_STEP':
      return { ...state, currentStep: Math.max(0, state.currentStep - 1), waitingForInteraction: false };
    case 'WAIT_INTERACTION':
      return { ...state, waitingForInteraction: true };
    case 'FINISH':
      return { ...initialState, showFinish: true };
    case 'STOP':
      return { ...initialState };
    case 'DISMISS_FINISH':
      return { ...state, showFinish: false };
    default:
      return state;
  }
}

const TutorialProvider = ({ children }: { children?: ReactNode }) => {
  const [state, dispatch] = useReducer(tutorialReducer, initialState);
  const [showWelcome, setShowWelcome] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isPWA = useIsPWA();
  const hasApporteur43397 = hasContractApporteur43397(useContractsData());
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Compute steps dynamically based on user access
  const steps = useMemo(() => getTutorialSteps(hasApporteur43397), [hasApporteur43397]);

  const currentStepData = state.isActive ? steps[state.currentStep] : null;

  // Resolve the correct target selector based on what actually exists in the DOM
  const getTargetSelector = useCallback(
    (step: TutorialStep | null | undefined) => {
      if (!step) return null;
      const isMobile = isPWA || window.innerWidth < 1024;

      // On mobile, try the mobile selector first, fall back to default
      if (isMobile && step.targetMobile) {
        if (document.querySelector(step.targetMobile)) return step.targetMobile;
      }

      return step.target;
    },
    [isPWA],
  );

  const completeTutorial = useCallback(() => {
    dispatch({ type: 'FINISH' });
    localStorage.setItem(STORAGE_KEY, 'true');
  }, []);

  const dismissFinish = useCallback(() => {
    dispatch({ type: 'DISMISS_FINISH' });
  }, []);

  const startTutorial = useCallback(() => {
    if (location.pathname !== '/') {
      navigate('/');
    }
    // Small delay to ensure we're on the home page
    setTimeout(() => {
      dispatch({ type: 'START' });
    }, location.pathname !== '/' ? 500 : 50);
  }, [location.pathname, navigate]);

  const requestTutorial = useCallback(() => {
    if (location.pathname !== '/') {
      navigate('/');
    }
    setTimeout(() => {
      setShowWelcome(true);
    }, location.pathname !== '/' ? 500 : 50);
  }, [location.pathname, navigate]);

  const prevStep = useCallback(() => {
    if (state.currentStep <= 0) return;
    dispatch({ type: 'PREV_STEP' });
  }, [state.currentStep]);

  const skipTutorial = useCallback(() => {
    dispatch({ type: 'STOP' });
    localStorage.setItem(STORAGE_KEY, 'true');
  }, []);

  const nextStep = useCallback(() => {
    const step = steps[state.currentStep];

    // If current step is the last one, complete
    if (step?.isLast || state.currentStep >= steps.length - 1) {
      completeTutorial();
      return;
    }

    const nextStepData = steps[state.currentStep + 1];

    // If next step is interactive, set waiting state
    if (nextStepData?.type === 'interactive') {
      dispatch({ type: 'NEXT_STEP' });
      // Wait a frame then set interaction mode
      requestAnimationFrame(() => {
        dispatch({ type: 'WAIT_INTERACTION' });
      });
      return;
    }

    // If next step is on a different page, we need to check after navigation
    dispatch({ type: 'NEXT_STEP' });
  }, [state.currentStep, steps, completeTutorial]);

  // Watch for route changes during interactive steps
  useEffect(() => {
    if (!state.isActive || !state.waitingForInteraction) return;

    const step = steps[state.currentStep];
    if (step?.type !== 'interactive' || !step.navigateTo) return;

    // Check if we've navigated to the expected page
    if (location.pathname.startsWith(step.navigateTo)) {
      // Clear any safety timeout
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }

      // Wait for page transition to finish (CSSTransition = 300ms)
      setTimeout(() => {
        dispatch({ type: 'NEXT_STEP' });
      }, 400);
    }
  }, [location.pathname, state.isActive, state.waitingForInteraction, state.currentStep, steps]);

  // Safety timeout for interactive steps
  useEffect(() => {
    if (!state.waitingForInteraction) return;

    navigationTimeoutRef.current = setTimeout(() => {
      // Auto-advance if user hasn't interacted within 15s
      dispatch({ type: 'NEXT_STEP' });
    }, 15000);

    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [state.waitingForInteraction]);

  // Skip steps whose target is missing from the DOM
  useEffect(() => {
    if (!state.isActive || state.waitingForInteraction) return;

    const step = steps[state.currentStep];
    if (!step) return;

    // Give DOM time to render after page transition
    const timer = setTimeout(() => {
      const selector = getTargetSelector(step);
      const el = selector ? document.querySelector(selector) : null;
      // Also check for hidden elements (zero dimensions)
      const isVisible = el instanceof HTMLElement && (el.offsetWidth > 0 || el.offsetHeight > 0);

      if (!isVisible && step.skipIfMissing) {
        // Skip this step
        if (state.currentStep >= steps.length - 1) {
          completeTutorial();
        } else {
          dispatch({ type: 'NEXT_STEP' });
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [state.isActive, state.currentStep, state.waitingForInteraction, steps, getTargetSelector, completeTutorial]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const value: TutorialContextValue = {
    isActive: state.isActive,
    currentStep: state.currentStep,
    currentStepData,
    waitingForInteraction: state.waitingForInteraction,
    totalSteps: steps.length,
    startTutorial,
    requestTutorial,
    showWelcome,
    setShowWelcome,
    nextStep,
    prevStep,
    skipTutorial,
    showFinish: state.showFinish,
    dismissFinish,
    getTargetSelector,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
      {state.isActive && <TutorialOverlay />}
      <WelcomePopup />
      <FinishPopup />
    </TutorialContext.Provider>
  );
};

export default TutorialProvider;
